// SPDX-License-Identifier: AGPL-3.0-or-later

import GuildMembers from '@app/features/member/state/GuildMembers';
import {createTask, loadGuildTasks} from '@app/features/tasks/commands/TaskCommands';
import Tasks from '@app/features/tasks/state/Tasks';
import {matchPerson, parseTrelloExport} from '@app/features/tasks/utils/TrelloImport';

export interface TrelloImportResult {
	added: number;
	skipped: number;
	failed: number;
}

const PAUSE_MS = 200;

/** Adds every card from a Trello export to this team's board. Cards imported before are skipped. */
export async function importTrelloBoard(
	guildId: string,
	file: File,
	onProgress?: (done: number, total: number) => void,
): Promise<TrelloImportResult> {
	const cards = parseTrelloExport(JSON.parse(await file.text()));
	await loadGuildTasks(guildId, {force: true});
	const existing = new Set(
		Tasks.getAllGuildTasks(guildId)
			.map((task) => /From Trello: (\S+)/.exec(task.notes ?? '')?.[1])
			.filter((url): url is string => !!url),
	);
	const people = GuildMembers.getMembers(guildId).map((member) => ({
		id: member.user.id,
		names: [member.nick, member.user.globalName, member.user.username].filter((n): n is string => !!n),
	}));
	const result: TrelloImportResult = {added: 0, skipped: 0, failed: 0};
	for (let i = 0; i < cards.length; i++) {
		const card = cards[i];
		onProgress?.(i, cards.length);
		if (card.sourceUrl && existing.has(card.sourceUrl)) {
			result.skipped++;
			continue;
		}
		const assignee = card.memberNames.map((name) => matchPerson(name, people)).find((id) => id != null) ?? null;
		const created = await createTask(guildId, {
			title: card.title,
			notes: card.notes,
			status: card.status,
			due_date: card.dueDate,
			assignee_id: assignee,
		});
		if (created) result.added++;
		else result.failed++;
		await new Promise((resolve) => setTimeout(resolve, PAUSE_MS));
	}
	onProgress?.(cards.length, cards.length);
	return result;
}
